<template>
    <div class="flex min-h-screen bg-gray-50">
      <!-- Sidebar Navigation -->
      <Sidebar
        :pendingApprovalsCount="pendingApprovalsCount"
        @open-create-modal="openCreateModal"
        @logout="logout"
      />

      <!-- Main Content -->
      <div class="lg:ml-64 p-4 sm:p-6 lg:p-8 w-full">
        <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <h1 class="text-2xl sm:text-3xl font-bold text-gray-800">Welcome, {{ user.name }}</h1>
          <div class="relative">
            <button @click="toggleNotifications" class="relative p-2 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors duration-200">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 01-6 0v-1m6 0H9"/>
              </svg>
              <span v-if="unreadNotificationsCount > 0"
                    class="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {{ unreadNotificationsCount }}
              </span>
            </button>

            <!-- Notifications Backdrop (Mobile Only) -->
            <div
              v-if="showNotifications"
              @click="showNotifications = false"
              class="sm:hidden fixed inset-0 bg-black bg-opacity-30 z-40"
            ></div>

            <!-- Notifications Dropdown -->
            <Notifications
              v-if="showNotifications"
              :notifications="notifications"
              @mark-read="markNotificationRead"
              @close="showNotifications = false"
              class="fixed sm:absolute top-16 sm:top-12 right-2 sm:right-0 z-50 max-w-[calc(100vw-1rem)] sm:max-w-none"
            />
          </div>
        </div>

        <!-- Connection Status -->
        <!-- <div v-if="!socketConnected" class="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p class="text-yellow-800 text-sm">⚠️ Real-time notifications disconnected. Trying to reconnect...</p>
        </div> -->

        <!-- Toast Notification -->
        <div v-if="showToast"
             class="fixed top-4 left-1/2 transform -translate-x-1/2 sm:left-auto sm:right-4 sm:transform-none bg-green-500 text-white px-4 sm:px-6 py-3 rounded-lg shadow-lg z-50 transition-all duration-300 text-sm sm:text-base">
          {{ toastMessage }}
        </div>
  
        <!-- Dashboard Content -->
        <div class="bg-white p-6 rounded-lg shadow-lg">
          <h2 class="text-xl font-bold text-gray-800 mb-4">Dashboard</h2>
          <p class="text-gray-600">Use the navigation menu to view your request history, pending approvals, or create a new cash request.</p>
          
          <!-- Quick Stats -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div class="bg-blue-50 p-4 rounded-lg">
              <div class="text-blue-600 text-sm font-medium">Total Notifications</div>
              <div class="text-2xl font-bold text-blue-800">{{ notifications.length }}</div>
            </div>
            <div class="bg-red-50 p-4 rounded-lg">
              <div class="text-red-600 text-sm font-medium">Unread</div>
              <div class="text-2xl font-bold text-red-800">{{ unreadNotificationsCount }}</div>
            </div>
            <div v-if="user.role !== 'employee' && user.role !== 'admin'" class="bg-green-50 p-4 rounded-lg">
              <div class="text-green-600 text-sm font-medium">Pending Approvals</div>
              <div class="text-2xl font-bold text-green-800">{{ pendingApprovalsCount }}</div>
            </div>
          </div>
        </div>
  
        <!-- Create Request Modal -->
        <div v-if="showCreateModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div class="bg-white bg-opacity-90 backdrop-blur-lg rounded-lg p-4 sm:p-6 w-full max-w-2xl my-8">
            <CreateRequest @request-created="fetchRequestsAndCloseModal" @close="closeCreateModal" />
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import { io } from 'socket.io-client';
  import CreateRequest from '../components/CreateRequest.vue';
  import AdminPanel from '../components/AdminPanel.vue';
  import Notifications from '../components/Notifications.vue';
  import Sidebar from '../components/Sidebar.vue';

  export default {
    components: { CreateRequest, AdminPanel, Notifications, Sidebar },
    data() {
      return {
        user: JSON.parse(localStorage.getItem('user') || '{}'),
        notifications: [],
        showNotifications: false,
        showCreateModal: false,
        socket: null,
        socketConnected: false,
        pendingApprovalsCount: 0,
        showToast: false,
        toastMessage: ''
      };
    },
    computed: {
      unreadNotificationsCount() {
        return this.notifications.filter(n => !n.read).length;
      },
    },
    async mounted() {
      await this.fetchNotifications();
      await this.fetchPendingApprovals();
     // this.initializeSocket();
    },
    beforeUnmount() {
      if (this.socket) {
        this.socket.disconnect();
      }
    },
    methods: {
      initializeSocket() {
        const token = localStorage.getItem('token');
        if (!token) return;

        this.socket = io('https://guarantee.iwacufinance.com/cash-request', {
          path: '/cash-request/socket.io',
          auth: { token },
          transports: ['websocket', 'polling']
        });

        this.socket.on('connect', () => {
          console.log('Connected to socket server');
          this.socketConnected = true;
        });

        this.socket.on('disconnect', () => {
          console.log('Disconnected from socket server');
          this.socketConnected = false;
        });

        this.socket.on('notification', (notification) => {
          // Only handle notifications meant for current user
          if (notification.userId === this.user.id) {
            // Add to notifications list
            this.notifications.unshift({
              _id: notification.id,
              message: notification.message,
              type: notification.type,
              read: notification.read,
              timestamp: notification.createdAt
            });

            // Show toast notification
            this.showToastNotification(notification.message);

            // Update pending approvals count if needed
            if (notification.type === 'new_request' && this.user.role !== 'employee' && this.user.role !== 'admin') {
              this.fetchPendingApprovals();
            }
          }
        });

        this.socket.on('connect_error', (error) => {
          console.error('Socket connection error:', error);
          this.socketConnected = false;
        });
      },

      showToastNotification(message) {
        this.toastMessage = message;
        this.showToast = true;
        setTimeout(() => {
          this.showToast = false;
        }, 5000);
      },

      async fetchNotifications() {
        try {
          const res = await axios.get('https://guarantee.iwacufinance.com/cash-request/api/notifications', {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          });
          this.notifications = res.data;
        } catch (error) {
          console.error('Error fetching notifications:', error);
        }
      },

      async fetchPendingApprovals() {
        if (this.user.role === 'employee' || this.user.role === 'admin') return;
        
        try {
          const res = await axios.get('https://guarantee.iwacufinance.com/cash-request/api/requests/pending', {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          });
          this.pendingApprovalsCount = res.data.length;
        } catch (error) {
          console.error('Error fetching pending approvals:', error);
        }
      },

      async markNotificationRead(id) {
        try {
          await axios.post(`https://guarantee.iwacufinance.com/cash-request/api/notifications/${id}/read`, {}, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          });
          this.notifications = this.notifications.map(n => 
            n._id === id ? { ...n, read: true } : n
          );
        } catch (error) {
          console.error('Error marking notification as read:', error);
        }
      },

      toggleNotifications() {
        this.showNotifications = !this.showNotifications;
      },

      openCreateModal() {
        this.showCreateModal = true;
      },

      closeCreateModal() {
        this.showCreateModal = false;
      },

      fetchRequestsAndCloseModal() {
        this.closeCreateModal();
        // Refresh notifications after creating a request
        this.fetchNotifications();
      },

      logout() {
        if (this.socket) {
          this.socket.disconnect();
        }
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        this.$router.push('/');
      },
    },
  };
  </script>

<style scoped>
.stat-card {
  @apply bg-white rounded-2xl p-6 shadow-sm border border-gray-100 transition-all duration-300;
}

.stat-icon {
  @apply w-12 h-12 rounded-xl text-white flex items-center justify-center shadow-lg;
}

.action-btn {
  @apply flex items-center justify-center px-6 py-3 rounded-xl font-semibold transition-all duration-200 shadow-sm hover:shadow-md;
}
</style>